import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  List,
  Modal,
  Row,
  Space,
  Spin,
  Switch,
  Table,
  Typography,
  message,
  Select,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  CloudDownloadOutlined,
  CloudUploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useI18n } from '../hooks/useI18n';
import {
  listUserPermissions,
  getUserPermissions,
  upsertUserPermission,
  deleteUserPermission,
  deleteAllPermissionsForUser,
  restoreUserPermissionsFromRemote,
  syncUserPermissionsRemote,
  UserPermissionRecord,
  UserPermissionSummary
} from '../services/userPermissionsService';
import { companyService, Company, buildProjectIdSelectOptions } from '../services/companyService';

interface PermissionRow extends UserPermissionRecord {
  key: string;
}

const { Title, Text } = Typography;

const UserPermissionsManager: React.FC = () => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [users, setUsers] = useState<UserPermissionSummary[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [addForm] = Form.useForm<{ permissionName: string; granted: boolean }>();
  const [restoringRemote, setRestoringRemote] = useState(false);
  const [syncingRemote, setSyncingRemote] = useState(false);
  const [projectScope, setProjectScope] = useState<string>('all');
  const [userListFilter, setUserListFilter] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);

  const projectSelectOptions = useMemo(
    () => buildProjectIdSelectOptions(companies, t('common.all')),
    [companies, t]
  );

  useEffect(() => {
    if (projectScope === 'all') return;
    const ok = projectSelectOptions.some((o) => o.value === projectScope);
    if (!ok) setProjectScope('all');
  }, [projectScope, projectSelectOptions]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await listUserPermissions(projectScope);
      setUsers(data);
      if (!selectedUser && data.length > 0) {
        setSelectedUser(data[0].userName);
      }
    } catch (error: any) {
      message.error(error?.message || t('userPermissions.message.loadUsersFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadUserPermissions = async (userName: string) => {
    setUserLoading(true);
    try {
      const data = await getUserPermissions(userName, projectScope);
      setPermissions(
        data.map(item => ({
          ...item,
          granted: !!item.granted,
          key: `${item.permission_name}`
        }))
      );
    } catch (error: any) {
      message.error(error?.message || t('userPermissions.message.loadUserFailed'));
    } finally {
      setUserLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    // projectScope 变化时，按 project 重新拉取用户列表
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectScope]);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const currentTenant = localStorage.getItem('currentTenant') || 'default';
        const res = await companyService.getCompanies(currentTenant);
        if (res?.success) {
          setCompanies(res.data || []);
          if (projectScope === 'all' && (res.data || []).length > 0) {
            const firstPid = (res.data as Company[]).find(
              (c) => c.project_id != null && String(c.project_id).trim() !== ''
            )?.project_id;
            if (firstPid != null) setProjectScope(String(firstPid));
          }
        }
      } catch {
        // ignore
      }
    };
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      loadUserPermissions(selectedUser);
    } else {
      setPermissions([]);
    }
  }, [selectedUser, projectScope]);

  const filteredUsers = useMemo(() => {
    const kw = userListFilter.trim().toLowerCase();
    if (!kw) return users;
    return users.filter(u => u.userName.toLowerCase().includes(kw));
  }, [users, userListFilter]);

  useEffect(() => {
    if (!selectedUser) return;
    if (!filteredUsers.some(u => u.userName === selectedUser)) {
      setSelectedUser(filteredUsers[0]?.userName ?? null);
    }
  }, [filteredUsers, selectedUser]);

  const totalPermissionSlots = useMemo(
    () => users.reduce((s, u) => s + u.total, 0),
    [users]
  );
  const totalGrantedAcrossUsers = useMemo(
    () => users.reduce((s, u) => s + u.grantedCount, 0),
    [users]
  );

  const handleTogglePermission = async (record: PermissionRow, granted: boolean) => {
    try {
      await upsertUserPermission({
        userName: record.user_name,
        permissionName: record.permission_name,
        granted
      }, projectScope);
      setPermissions(prev => prev.map(item => item.key === record.key ? { ...item, granted } : item));
      message.success(t('userPermissions.message.updateSuccess'));
      loadUsers();
    } catch (error: any) {
      message.error(error?.message || t('userPermissions.message.updateFailed'));
    }
  };

  const handleRemovePermission = async (record: PermissionRow) => {
    Modal.confirm({
      title: t('userPermissions.confirm.deleteTitle'),
      content: t('userPermissions.confirm.deleteContent', { permission: record.permission_name }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await deleteUserPermission(record.user_name, record.permission_name, projectScope);
          setPermissions(prev => prev.filter(item => item.key !== record.key));
          message.success(t('userPermissions.message.deleteSuccess'));
          loadUsers();
        } catch (error: any) {
          message.error(error?.message || t('userPermissions.message.deleteFailed'));
        }
      }
    });
  };

  const handleClearPermissions = async () => {
    if (!selectedUser) return;
    Modal.confirm({
      title: t('userPermissions.confirm.clearTitle'),
      content: t('userPermissions.confirm.clearContent', { user: selectedUser }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await deleteAllPermissionsForUser(selectedUser, projectScope);
          setPermissions([]);
          message.success(t('userPermissions.message.clearSuccess'));
          loadUsers();
        } catch (error: any) {
          message.error(error?.message || t('userPermissions.message.clearFailed'));
        }
      }
    });
  };

  const handleRestoreFromRemote = () => {
    Modal.confirm({
      title: t('userPermissions.confirm.restoreTitle'),
      content: t('userPermissions.confirm.restoreContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setRestoringRemote(true);
        try {
          const result = await restoreUserPermissionsFromRemote(projectScope);
          // 仅刷新当前 project 的用户权限
          message.success(result?.message || t('userPermissions.message.restoreSuccess'));
          setSelectedUser(null);
          await loadUsers();
        } catch (error: any) {
          message.error(error?.response?.data?.message || t('userPermissions.message.restoreFailed'));
        } finally {
          setRestoringRemote(false);
        }
      }
    });
  };

  const handleSyncRemoteUpdate = () => {
    Modal.confirm({
      title: t('userPermissions.confirm.updateRemoteTitle'),
      content: t('userPermissions.confirm.updateRemoteContent'),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setSyncingRemote(true);
        try {
          const result = await syncUserPermissionsRemote(projectScope);
          message.success(result?.message || t('userPermissions.message.updateRemoteSuccess'));
        } catch (error: any) {
          message.error(error?.response?.data?.message || t('userPermissions.message.updateRemoteFailed'));
        } finally {
          setSyncingRemote(false);
        }
      }
    });
  };

  const showAddModal = () => {
    addForm.resetFields();
    addForm.setFieldsValue({ granted: true });
    setIsAddModalVisible(true);
  };

  const handleAddPermission = () => {
    addForm
      .validateFields()
      .then(async values => {
        if (!selectedUser) {
          message.warning(t('userPermissions.message.selectUserFirst'));
          return;
        }
        try {
          await upsertUserPermission({
            userName: selectedUser,
            permissionName: values.permissionName,
            granted: values.granted
          }, projectScope);
          message.success(t('userPermissions.message.addSuccess'));
          setIsAddModalVisible(false);
          loadUserPermissions(selectedUser);
          loadUsers();
        } catch (error: any) {
          message.error(error?.message || t('userPermissions.message.addFailed'));
        }
      })
      .catch(() => undefined);
  };

  const permissionColumns = useMemo(() => [
    {
      title: t('userPermissions.columns.permissionName'),
      dataIndex: 'permission_name'
    },
    {
      title: t('userPermissions.columns.granted'),
      dataIndex: 'granted',
      render: (_: boolean, record: PermissionRow) => (
        <Switch
          checked={record.granted}
          onChange={(checked) => handleTogglePermission(record, checked)}
        />
      )
    },
    {
      title: t('common.actions'),
      dataIndex: 'actions',
      render: (_: any, record: PermissionRow) => (
        <Button danger type="link" onClick={() => handleRemovePermission(record)}>
          {t('common.delete')}
        </Button>
      )
    }
  ], [handleTogglePermission, t]);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Row align="middle" justify="start" style={{ flexWrap: 'wrap' }}>
          <Space align="baseline" size={16} style={{ flexWrap: 'wrap' }}>
            <Title level={2} style={{ margin: 0 }}>
              <UserOutlined style={{ marginRight: '8px' }} />
              {t('userPermissions.title')}
            </Title>
            <Select
              showSearch
              optionFilterProp="label"
              style={{ width: 280 }}
              value={projectScope}
              onChange={setProjectScope}
              options={projectSelectOptions}
            />
            <Text type="secondary">Version: —</Text>
            <Text type="secondary">{t('userPermissions.subtitle')}</Text>
            <Space style={{ flexWrap: 'wrap' }}>
              <Button icon={<ReloadOutlined />} onClick={loadUsers} loading={loading}>
                {t('common.refresh')}
              </Button>
              <Button icon={<CloudDownloadOutlined />} loading={restoringRemote} onClick={handleRestoreFromRemote}>
                {t('userPermissions.actions.restoreRemote')}
              </Button>
              <Button icon={<CloudUploadOutlined />} loading={syncingRemote} onClick={handleSyncRemoteUpdate}>
                {t('userPermissions.actions.updateRemote')}
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal} disabled={!selectedUser}>
                {t('userPermissions.actions.addPermission')}
              </Button>
              <Button danger onClick={handleClearPermissions} disabled={!selectedUser || permissions.length === 0}>
                {t('userPermissions.actions.clearPermissions')}
              </Button>
            </Space>
          </Space>
        </Row>
      </div>

      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title={t('userPermissions.stat.users')}
              value={users.length}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic title={t('userPermissions.stat.permissionSlots')} value={totalPermissionSlots} />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card>
            <Statistic
              title={t('userPermissions.stat.granted')}
              value={totalGrantedAcrossUsers}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: '24px' }}>
        <Input.Search
          allowClear
          placeholder={t('userPermissions.searchUsersPlaceholder')}
          value={userListFilter}
          onChange={(e) => setUserListFilter(e.target.value)}
        />
      </Card>

      <Card>
        <Row gutter={16}>
          <Col span={8}>
            <Card size="small" title={t('userPermissions.sections.users')} bordered={false}>
              <Spin spinning={loading}>
                <List
                  dataSource={filteredUsers}
                  size="small"
                  locale={{ emptyText: t('userPermissions.table.empty') }}
                  renderItem={(item) => (
                    <List.Item
                      onClick={() => setSelectedUser(item.userName)}
                      style={{
                        cursor: 'pointer',
                        background: item.userName === selectedUser ? 'var(--tcc-list-active-bg, #e6f7ff)' : undefined
                      }}
                    >
                      <Space direction="vertical" size={0}>
                        <Text strong>{item.userName}</Text>
                        <Text type="secondary">
                          {t('userPermissions.summary.tag', { granted: item.grantedCount, total: item.total })}
                        </Text>
                      </Space>
                    </List.Item>
                  )}
                />
              </Spin>
            </Card>
          </Col>
          <Col span={16}>
            <Card
              size="small"
              title={
                selectedUser
                  ? t('userPermissions.sections.permissions', { user: selectedUser })
                  : t('userPermissions.sections.noUserSelected')
              }
              bordered={false}
            >
              {selectedUser ? (
                <Spin spinning={userLoading}>
                  <Table
                    dataSource={permissions}
                    columns={permissionColumns}
                    pagination={false}
                    rowKey="key"
                    locale={{ emptyText: t('userPermissions.table.empty') }}
                  />
                </Spin>
              ) : (
                <Text type="secondary">{t('userPermissions.message.selectUserFirst')}</Text>
              )}
            </Card>
          </Col>
        </Row>
      </Card>

      <Modal
        open={isAddModalVisible}
        title={t('userPermissions.modal.addTitle', { user: selectedUser })}
        onOk={handleAddPermission}
        onCancel={() => setIsAddModalVisible(false)}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="permissionName"
            label={t('userPermissions.fields.permissionName')}
            rules={[{ required: true, message: t('userPermissions.validation.permissionRequired') }]}
          >
            <Input placeholder={t('userPermissions.placeholders.permissionName')} />
          </Form.Item>
          <Form.Item
            name="granted"
            label={t('userPermissions.fields.granted')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserPermissionsManager;
